import { useState, useEffect, ReactNode } from "react";
import { DwhDataContext, DwhDataContextType, DwhData } from "./DwhDataContext";
import { runBigQueryQuery } from "../bigquery/BigQueryClient";
import { DWH_QUERIES } from "../config/dwhQueries";
import { useAuth } from "../auth/AuthContext";
import { useControl } from "../control/ControlContext";

export function DwhDataProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, requiresAuth } = useAuth();
  const { timeRange } = useControl();

  const [dwhData, setDwhData] = useState<DwhData | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (requiresAuth && !isAuthenticated) return;
    if (!timeRange.start || !timeRange.end) return;

    const startDate = timeRange.start.toISOString().split("T")[0];
    const endDate = timeRange.end.toISOString().split("T")[0];

    setIsLoading(true);
    setError(null);

    // Always store daily data — resampling to weekly is done in DwhPlots so the grain
    // toggle can switch between day/week without reloading data.
    const fetchAll = async (): Promise<DwhData> => {
      const result: DwhData = {};
      for (const queryDef of DWH_QUERIES) {
        const sql = queryDef.sql.replace(/\{startDate\}/g, startDate).replace(/\{endDate\}/g, endDate);
        const rows = await runBigQueryQuery(sql, queryDef.billingProject);
        const dateCol = queryDef.dateColumn ?? "day";

        result[queryDef.id] = {};
        for (const col of queryDef.columns) {
          const timestamps: Date[] = [];
          const data: (number | null)[] = [];
          for (const row of rows) {
            const dateVal = row[dateCol];
            if (dateVal == null) continue;
            timestamps.push(new Date(String(dateVal)));
            const val = row[col.key];
            data.push(val != null ? Number(val) : null);
          }
          result[queryDef.id][col.key] = { timestamps, data };
        }
      }
      return result;
    };

    let cancelled = false;
    fetchAll()
      .then(data => {
        if (!cancelled) setDwhData(data);
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load DWH data");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, requiresAuth, timeRange]);

  const contextValue: DwhDataContextType = { dwhData, isLoading, error };
  return <DwhDataContext.Provider value={contextValue}>{children}</DwhDataContext.Provider>;
}
